import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import { z } from "zod";

import { NIKE_URL } from "@/data/constants/url";
import { useAuth } from "@/features/auth/shared/hooks/use-auth";
import { useLang } from "@/hooks/use-lang";
import { Lang } from "@/i18n-config";
import { createErrorMessage } from "@/utils/create-error-message";

import { SwingInfo, TrendInfo, ValueInfo } from "../../services/symbol-content";

type BacktestActionsParams = {
  lang: Lang;
  symbol: string;
  token: string;
  oriented: "value" | "swing" | "trend";
  strategy: ValueInfo | SwingInfo | TrendInfo;
  start_at: string;
  end_at: string;
};

const backtestActionSchema = z.object({
  data: z.array(
    z
      .object({
        date: z.string(),
        action: z.enum(["BUY", "SELL"]),
      })
      .partial()
  ),
  message: z.string(),
});

export type BacktestAction = z.infer<
  typeof backtestActionSchema
>["data"][number];

export const getBacktestActions = async ({
  lang,
  symbol,
  token,
  oriented,
  strategy,
  start_at,
  end_at,
}: BacktestActionsParams): Promise<BacktestAction[]> => {
  const endpoint = `/backtest-action/${symbol}/${oriented}/${strategy}/${start_at}/${end_at}`;
  const res = await fetch(`${NIKE_URL}${endpoint}`, {
    method: "GET",
    headers: {
      "Accept-Language": lang,
      "X-User-Profile": token,
    },
    cache: "no-cache",
  });
  if (!res.ok) {
    const errorMessage = await createErrorMessage(endpoint, res);
    throw new Error(errorMessage);
  }
  return backtestActionSchema.parse(await res.json()).data;
};

export const useBacktestActions = ({
  symbol,
  oriented,
  strategy,
  start_at,
  end_at,
}: Omit<BacktestActionsParams, "lang" | "token" | "start_at" | "end_at"> &
  Partial<Pick<BacktestActionsParams, "start_at" | "end_at">>) => {
  const { lang } = useLang();
  const { backendToken } = useAuth();
  return useQuery(
    [
      "backtest-action",
      lang,
      symbol,
      backendToken,
      oriented,
      strategy,
      start_at,
      end_at,
    ],
    () =>
      getBacktestActions({
        lang,
        symbol,
        token: backendToken ?? "",
        oriented,
        strategy,
        start_at: start_at ?? "2007-01-01",
        end_at: end_at ?? dayjs().format("YYYY-MM-DD"),
      }),
    { enabled: !!symbol && !!backendToken && !!oriented && !!strategy }
  );
};
